// src/pages/admin/StaffManagementPage.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStaff } from '../../contexts/StaffContext';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase-config';
import { collection, getDocs } from 'firebase/firestore';
import HoursAccountService from '../../services/HoursAccountService';
import ConfirmModal from '../../components/common/ConfirmModal';
import '../../styles/staff-core.css';

// ---------------------------------------------------------------------------
// Lightweight hook — avoids pulling in an external library for one value.
// Initialises from window.innerWidth so the first render is already correct.
// ---------------------------------------------------------------------------
const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const StaffManagementPage = () => {
  const { t } = useTranslation('staff');
  const {
    // ARCHIVE: swapped `staff` for the two pre-split lists + archive actions
    activeStaff,
    archivedStaff,
    archiveStaffMember,
    reactivateStaffMember,
    deleteStaffMember,
    loading: staffLoading,
    error: staffError,
    fetchAllStaff,
    searchStaff
  } = useStaff();
  const { currentUser, currentCompany, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Breakpoint flags derived from live window width
  const windowWidth  = useWindowWidth();
  const isMobile     = windowWidth < 600;   // stack header + filter rows
  const isTablet     = windowWidth < 768;   // switch table → card list

  const [searchTerm,        setSearchTerm]        = useState('');
  const [departmentFilter,  setDepartmentFilter]  = useState('');
  const [filteredStaff,     setFilteredStaff]     = useState([]);
  const [departments,       setDepartments]       = useState([]);
  const [loadingDepartments,setLoadingDepartments]= useState(true);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState(null);

  // ARCHIVE: view toggle + derived source list
  const [showArchived, setShowArchived] = useState(false);
  const sourceStaff = showArchived ? archivedStaff : activeStaff;

  // ARCHIVE: which confirm modal is open, if any — { type: 'archive'|'delete'|'reactivate', staffId }
  const [modal, setModal] = useState(null);

  // -------------------------------------------------------------------------
  // Hours Account — ytdBalance per staff member (read-only, stored values)
  // -------------------------------------------------------------------------
  const [hoursBalances, setHoursBalances] = useState({}); // { [staffId]: number|null }
  const [hoursSortDirection, setHoursSortDirection] = useState(null); // null | 'asc' | 'desc'

  const currentYear = useMemo(() => new Date().getFullYear().toString(), []);

  const companyId =
    currentCompany?.id || (currentUser && currentUser.uid);

  // Debug
  useEffect(() => {
    console.log('Auth state:', {
      currentUser: currentUser ? 'Logged in' : 'Not logged in',
      currentCompany,
      companyId,
      authLoading
    });
  }, [currentUser, currentCompany, companyId, authLoading]);

  // -------------------------------------------------------------------------
  // Fetch departments
  // -------------------------------------------------------------------------
  const fetchDepartments = useCallback(async () => {
    if (!companyId) return;

    setLoadingDepartments(true);
    try {
      console.log('Fetching departments for company:', companyId);
      const departmentsRef = collection(
        db, 'companies', companyId, 'departments'
      );
      const snapshot = await getDocs(departmentsRef);

      const departmentsList = snapshot.docs.map(doc => ({
        id:   doc.id,
        name: doc.data().name || 'Unknown Department',
        ...doc.data()
      }));

      console.log(`Successfully loaded ${departmentsList.length} departments`);
      setDepartments(departmentsList);
      return departmentsList;
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError(`Failed to load departments: ${err.message}`);
      return [];
    } finally {
      setLoadingDepartments(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) fetchDepartments();
  }, [companyId, fetchDepartments]);

  // -------------------------------------------------------------------------
  // Department map
  // -------------------------------------------------------------------------
  const [departmentMap, setDepartmentMap] = useState({});

  useEffect(() => {
    if (departments && departments.length > 0) {
      const deptMap = {};
      departments.forEach(dept => {
        if (dept && dept.id) deptMap[dept.id] = dept.name;
      });
      setDepartmentMap(deptMap);
    }
  }, [departments]);

  // -------------------------------------------------------------------------
  // Fetch Hours Account ytdBalance for every staff member (current year only)
  // Reads only — never creates or writes a document. Staff without an
  // hoursAccounts/{year} doc yet resolve to null and render as "—".
  // -------------------------------------------------------------------------
  const fetchHoursBalances = useCallback(async (staffArray) => {
    if (!companyId || !staffArray || staffArray.length === 0) {
      setHoursBalances({});
      return;
    }

    try {
      const entries = await Promise.all(
        staffArray.map(async (s) => {
          try {
            const account = await HoursAccountService.getAccount(
              companyId, s.id, currentYear
            );
            return [s.id, account ? account.ytdBalance : null];
          } catch (err) {
            console.error(
              `Failed to load hours account for staff ${s.id}:`, err
            );
            return [s.id, null];
          }
        })
      );

      setHoursBalances(Object.fromEntries(entries));
    } catch (err) {
      // Promise.all itself won't reject given the inner try/catch above,
      // but keep a guard in case HoursAccountService signature changes.
      console.error('Error fetching hours balances:', err);
    }
  }, [companyId, currentYear]);

  // ARCHIVE: fetch balances for whichever list is currently shown
  useEffect(() => {
    if (sourceStaff && sourceStaff.length > 0) {
      fetchHoursBalances(sourceStaff);
    } else {
      setHoursBalances({});
    }
  }, [sourceStaff, fetchHoursBalances]);

  // -------------------------------------------------------------------------
  // Process staff
  // -------------------------------------------------------------------------
  const processStaffData = useCallback(() => {
    // ARCHIVE: sourceStaff instead of staff
    if (!sourceStaff || sourceStaff.length === 0) {
      setFilteredStaff([]);
      return;
    }

    try {
      let results = [...sourceStaff];

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        results = results.filter(s =>
          (s.name  && s.name.toLowerCase().includes(term)) ||
          (s.email && s.email.toLowerCase().includes(term))
        );
      }

      if (departmentFilter) {
        results = results.filter(s =>
          s.departmentIds && s.departmentIds.includes(departmentFilter)
        );
      }

      const staffWithDepartments = results.map(staffMember => {
        let primaryDeptName = t('page.unassigned');

        if (
          staffMember.primaryDepartmentId &&
          departmentMap[staffMember.primaryDepartmentId]
        ) {
          primaryDeptName = departmentMap[staffMember.primaryDepartmentId];
        } else if (
          staffMember.departmentIds &&
          staffMember.departmentIds.length > 0
        ) {
          const firstDeptId = staffMember.departmentIds[0];
          if (departmentMap[firstDeptId]) {
            primaryDeptName = departmentMap[firstDeptId];
          }
        }

        return { ...staffMember, primaryDepartmentName: primaryDeptName };
      });

      setFilteredStaff(staffWithDepartments);
    } catch (err) {
      console.error('Error processing staff data:', err);
      setError(`Failed to process staff: ${err.message}`);
    }
  }, [sourceStaff, searchTerm, departmentFilter, departmentMap, t]);

  useEffect(() => {
    const initializeStaffData = async () => {
      if (!companyId || loadingDepartments) return;

      setLoading(true);
      try {
        if (typeof fetchAllStaff === 'function') await fetchAllStaff();
      } catch (err) {
        console.error('Error fetching staff data:', err);
        setError(`Failed to load staff: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    initializeStaffData();
  }, [companyId, loadingDepartments]); // eslint-disable-line react-hooks/exhaustive-deps

  // ARCHIVE: depend on sourceStaff (and re-run when the toggle flips)
  useEffect(() => {
    processStaffData();
  }, [sourceStaff, searchTerm, departmentFilter, departmentMap, processStaffData]);

  // -------------------------------------------------------------------------
  // Sorting — currently wired to the Hours Balance column only.
  // 3-state toggle: unsorted -> ascending -> descending -> unsorted.
  // Staff with no hours account (null balance) always sort to the bottom.
  // -------------------------------------------------------------------------
  const handleHoursSortClick = () => {
    setHoursSortDirection(prev => {
      if (prev === null)   return 'asc';
      if (prev === 'asc')  return 'desc';
      return null;
    });
  };

  const sortedStaff = useMemo(() => {
    const list = filteredStaff || [];

    if (!hoursSortDirection) return list;

    return [...list].sort((a, b) => {
      const aVal = hoursBalances[a.id];
      const bVal = hoursBalances[b.id];
      const aIsNull = aVal === null || aVal === undefined;
      const bIsNull = bVal === null || bVal === undefined;

      if (aIsNull && bIsNull) return 0;
      if (aIsNull) return 1;   // nulls always last
      if (bIsNull) return -1;  // nulls always last

      return hoursSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filteredStaff, hoursBalances, hoursSortDirection]);

  // -------------------------------------------------------------------------
  // Hours balance formatting helpers
  // -------------------------------------------------------------------------
  const formatHoursBalance = (value) => {
    if (value === null || value === undefined) return '—';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)} ${t('table.hours')}`;
  };

  const getHoursBalanceColor = (value) => {
    if (value === null || value === undefined) return '#6E6E73'; // unknown/no account
    if (value > 0) return '#1B873F'; // positive — readable green
    if (value < 0) return '#FF4747'; // negative — matches existing error/negative color
    return '#1C1C1E'; // exactly zero
  };

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------
  const handleRefresh = useCallback(() => {
    if (!companyId) {
      setError('Cannot refresh: No company ID available');
      return;
    }
    fetchDepartments();
    if (typeof fetchAllStaff === 'function') fetchAllStaff();
  }, [companyId, fetchDepartments, fetchAllStaff]);

  const handleAddStaff = () => navigate('/admin/staff/new');

  // ARCHIVE: archive / reactivate / permanent-delete handlers
  // These open a ConfirmModal instead of calling window.confirm/window.prompt.
  const handleArchive = useCallback((staffId) => {
    setModal({ type: 'archive', staffId });
  }, []);

  const confirmArchive = useCallback(async (staffId) => {
    setError(null);
    try {
      await archiveStaffMember(staffId);
    } catch (err) {
      // NOTE: StaffContext.archiveStaffMember throws Error('CANNOT_ARCHIVE_WHILE_CLOCKED_IN'),
      // not 'STAFF_CLOCKED_IN' — confirmed against the actual context source.
      setError(
        err.message === 'CANNOT_ARCHIVE_WHILE_CLOCKED_IN'
          ? t('archive.cannotArchiveWhileClockedIn')
          : err.message
      );
    } finally {
      setModal(null);
    }
  }, [archiveStaffMember, t]);

  const handleReactivate = useCallback((staffId) => {
    setModal({ type: 'reactivate', staffId });
  }, []);

  const confirmReactivate = useCallback(async (staffId, newPin) => {
    setError(null);
    try {
      await reactivateStaffMember(staffId, newPin);
    } catch (err) {
      // NOTE: StaffContext.reactivateStaffMember already runs the duplicate-PIN
      // check internally and throws Error('PIN_TAKEN') with a `.conflict = {id, name}`
      // property on collision — surface that name instead of the raw message.
      if (err.message === 'PIN_TAKEN' && err.conflict) {
        setError(t('validation.pinTakenInDepartment', { name: err.conflict.name }));
      } else {
        setError(err.message);
      }
    } finally {
      setModal(null);
    }
  }, [reactivateStaffMember, t]);

  const handlePermanentDelete = useCallback((staffId) => {
    setModal({ type: 'delete', staffId });
  }, []);

  const confirmPermanentDelete = useCallback(async (staffId) => {
    setError(null);
    try {
      await deleteStaffMember(staffId);
    } catch (err) {
      setError(err.message);
    } finally {
      setModal(null);
    }
  }, [deleteStaffMember, t]);

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------
  const staffList      = sortedStaff;
  const isLoading      = loading || staffLoading || loadingDepartments;
  const errorMessage   = error || staffError;

  // -------------------------------------------------------------------------
  // Styles — mobile-aware values are swapped via the breakpoint flags above
  // -------------------------------------------------------------------------
  const styles = {
    container: {
      backgroundColor: '#F9F9F6',
      padding: '20px',
      color: '#1C1C1E',
      minHeight: '100vh'
    },

    header: {
      display: 'flex',
      flexDirection:   isMobile ? 'column'       : 'row',
      justifyContent:  isMobile ? 'flex-start'   : 'space-between',
      alignItems:      isMobile ? 'flex-start'   : 'center',
      flexWrap:        'wrap',
      gap:             '12px',
      marginBottom:    '24px'
    },

    title: {
      fontSize:   '28px',
      fontWeight: 'bold',
      color:      '#1C1C1E',
      margin:     0
    },

    headerActions: {
      display: 'flex',
      gap:     '12px',
      width:   isMobile ? '100%' : 'auto'
    },

    refreshButton: {
      backgroundColor: '#EDEDED',
      color:           '#529EDF',
      border:          '1px solid #529EDF',
      padding:         '8px 16px',
      borderRadius:    '4px',
      cursor:          'pointer',
      fontWeight:      '500',
      flex:            isMobile ? '1' : 'none'
    },

    addButton: {
      backgroundColor: '#7AF560',
      color:           '#1C1C1E',
      border:          'none',
      padding:         '8px 16px',
      borderRadius:    '4px',
      cursor:          'pointer',
      fontWeight:      '500',
      flex:            isMobile ? '1' : 'none'
    },

    filterContainer: {
      display:        'flex',
      flexDirection:  isMobile ? 'column' : 'row',
      flexWrap:       'wrap',
      gap:            '12px',
      marginBottom:   '16px'
    },

    // ARCHIVE: toggle row, sits between filters and the table
    archiveToggleRow: {
      display:      'flex',
      alignItems:   'center',
      gap:          '8px',
      marginBottom: '16px',
      fontSize:     '14px',
      color:        '#1C1C1E'
    },

    searchInput: {
      flex:            '1',
      minWidth:        0,     // prevents flex child from overflowing its container
      padding:         '10px 14px',
      borderRadius:    '4px',
      border:          '1px solid #EDEDED',
      backgroundColor: 'white',
      fontSize:        '16px'
    },

    departmentSelect: {
      width:           isMobile ? '100%' : 'auto',
      minWidth:        isMobile ? 'unset' : '240px',
      padding:         '10px 14px',
      borderRadius:    '4px',
      border:          '1px solid #EDEDED',
      backgroundColor: 'white',
      fontSize:        '16px'
    },

    tableContainer: {
      backgroundColor: 'white',
      borderRadius:    '8px',
      boxShadow:       '0 2px 4px rgba(0,0,0,0.05)',
      overflowX:       'auto'
    },

    table: {
      width:           '100%',
      borderCollapse:  'collapse',
      minWidth:        '700px'
    },

    tableHeader: {
      backgroundColor: '#EDEDED',
      color:           '#1C1C1E',
      textAlign:       'left',
      padding:         '14px 16px',
      fontWeight:      '600',
      whiteSpace:      'nowrap'
    },

    sortableTableHeader: {
      backgroundColor: '#EDEDED',
      color:           '#1C1C1E',
      textAlign:       'left',
      padding:         '14px 16px',
      fontWeight:      '600',
      whiteSpace:      'nowrap',
      cursor:          'pointer',
      userSelect:      'none'
    },

    tableRow: {
      borderBottom: '1px solid #EDEDED'
    },

    tableCell: {
      padding: '14px 16px',
      color:   '#1C1C1E'
    },

    actionsCell: {
      display:  'flex',
      gap:      '8px',
      flexWrap: 'wrap'
    },

    viewButton: {
      backgroundColor: 'transparent',
      color:           '#529EDF',
      border:          'none',
      padding:         '6px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      textDecoration:  'none'
    },

    editButton: {
      backgroundColor: '#529EDF',
      color:           'white',
      border:          'none',
      padding:         '6px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      textDecoration:  'none'
    },

    // ARCHIVE: action button variants
    archiveButton: {
      backgroundColor: 'transparent',
      color:           '#FF4747',
      border:          '1px solid #FF4747',
      padding:         '6px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      fontSize:        '13px'
    },

    reactivateButton: {
      backgroundColor: '#1B873F',
      color:           'white',
      border:          'none',
      padding:         '6px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      fontSize:        '13px'
    },

    deleteButton: {
      backgroundColor: '#FF4747',
      color:           'white',
      border:          'none',
      padding:         '6px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      fontSize:        '13px'
    },

    archivedBadge: {
      display:         'inline-block',
      backgroundColor: '#EDEDED',
      color:           '#6E6E73',
      fontSize:        '11px',
      fontWeight:      '600',
      padding:         '2px 8px',
      borderRadius:    '10px',
      marginLeft:      '8px',
      textTransform:   'uppercase',
      letterSpacing:   '0.4px'
    },

    errorMessage: {
      backgroundColor: '#FF4747',
      color:           'white',
      padding:         '10px 16px',
      borderRadius:    '4px',
      marginBottom:    '16px'
    },

    noResults: {
      textAlign: 'center',
      padding:   '40px 16px',
      color:     '#6E6E73'
    },

    loadingIndicator: {
      textAlign: 'center',
      padding:   '40px 16px',
      color:     '#6E6E73'
    },

    cardList: {
      display:       'flex',
      flexDirection: 'column',
      gap:           '12px'
    },

    card: {
      backgroundColor: 'white',
      borderRadius:    '8px',
      boxShadow:       '0 2px 4px rgba(0,0,0,0.05)',
      padding:         '16px'
    },

    cardName: {
      fontSize:     '16px',
      fontWeight:   '600',
      color:        '#1C1C1E',
      marginBottom: '12px',
      display:      'flex',
      alignItems:   'center'
    },

    cardRow: {
      display:        'flex',
      justifyContent: 'space-between',
      alignItems:     'flex-start',
      paddingBottom:  '8px',
      marginBottom:   '8px',
      borderBottom:   '1px solid #EDEDED'
    },

    cardRowLast: {
      display:        'flex',
      justifyContent: 'space-between',
      alignItems:     'flex-start',
      paddingBottom:  '8px',
      marginBottom:   '8px',
      borderBottom:   '1px solid #EDEDED'
    },

    cardRowFinal: {
      display:        'flex',
      justifyContent: 'space-between',
      alignItems:     'flex-start'
    },

    cardLabel: {
      fontSize:      '12px',
      color:         '#6E6E73',
      fontWeight:    '500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      flex:          '0 0 40%'
    },

    cardValue: {
      fontSize:   '14px',
      color:      '#1C1C1E',
      textAlign:  'right',
      flex:       '0 0 58%',
      wordBreak:  'break-word'
    },

    cardActions: {
      display:      'flex',
      gap:          '8px',
      marginTop:    '14px',
      paddingTop:   '14px',
      borderTop:    '1px solid #EDEDED',
      flexWrap:     'wrap'
    },

    cardViewButton: {
      flex:            '1',
      backgroundColor: 'transparent',
      color:           '#529EDF',
      border:          '1px solid #529EDF',
      padding:         '8px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      textDecoration:  'none',
      textAlign:       'center',
      fontSize:        '14px',
      fontWeight:      '500'
    },

    cardEditButton: {
      flex:            '1',
      backgroundColor: '#529EDF',
      color:           'white',
      border:          'none',
      padding:         '8px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      textDecoration:  'none',
      textAlign:       'center',
      fontSize:        '14px',
      fontWeight:      '500'
    },

    // ARCHIVE: full-width mobile action button variants
    cardArchiveButton: {
      flex:            '1',
      backgroundColor: 'transparent',
      color:           '#FF4747',
      border:          '1px solid #FF4747',
      padding:         '8px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      textAlign:       'center',
      fontSize:        '14px',
      fontWeight:      '500'
    },

    cardReactivateButton: {
      flex:            '1',
      backgroundColor: '#1B873F',
      color:           'white',
      border:          'none',
      padding:         '8px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      textAlign:       'center',
      fontSize:        '14px',
      fontWeight:      '500'
    },

    cardDeleteButton: {
      flex:            '1',
      backgroundColor: '#FF4747',
      color:           'white',
      border:          'none',
      padding:         '8px 12px',
      borderRadius:    '4px',
      cursor:          'pointer',
      textAlign:       'center',
      fontSize:        '14px',
      fontWeight:      '500'
    }
  };

  // -------------------------------------------------------------------------
  // Sub-renderers — kept as named functions so the return JSX stays readable
  // -------------------------------------------------------------------------

  // Desktop table (>=768px)
  const renderTable = () => (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>{t('table.name')}</th>
            <th style={styles.tableHeader}>{t('table.role')}</th>
            <th style={styles.tableHeader}>{t('table.email')}</th>
            <th style={styles.tableHeader}>{t('table.primaryDepartment')}</th>
            <th style={styles.tableHeader}>{t('table.monthlyContractHours')}</th>
            <th
              style={styles.sortableTableHeader}
              onClick={handleHoursSortClick}
              title={t(
                'table.hoursBalanceTooltip',
                'Year-to-date hours balance — click to sort'
              )}
            >
              {t('table.hoursBalance', 'Hours Balance')}
              {hoursSortDirection === 'asc'  && ' \u25B2'}
              {hoursSortDirection === 'desc' && ' \u25BC'}
            </th>
            <th style={styles.tableHeader}>{t('table.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {staffList.map(staffMember => {
            const balance = hoursBalances[staffMember.id];
            return (
              <tr key={staffMember.id} style={styles.tableRow}>
                <td style={styles.tableCell}>
                  {staffMember.name}
                  {staffMember.active === false && (
                    <span style={styles.archivedBadge}>
                      {t('archive.archived')}
                    </span>
                  )}
                </td>
                <td style={styles.tableCell}>{t(`roles.${staffMember.role}`)}</td>
                <td style={styles.tableCell}>
                  {staffMember.email || t('table.noEmail')}
                </td>
                <td style={styles.tableCell}>
                  {staffMember.primaryDepartmentName}
                </td>
                <td style={styles.tableCell}>
                  {staffMember.employmentInfo?.contractHours || 0}{' '}
                  {t('table.hours')}
                </td>
                <td
                  style={{
                    ...styles.tableCell,
                    color:      getHoursBalanceColor(balance),
                    fontWeight: '600'
                  }}
                >
                  {formatHoursBalance(balance)}
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.actionsCell}>
                    <Link
                      to={`/admin/staff/${staffMember.id}`}
                      style={styles.viewButton}
                    >
                      {t('table.view')}
                    </Link>

                    {!showArchived && (
                      <>
                        <Link
                          to={`/admin/staff/${staffMember.id}/edit`}
                          style={styles.editButton}
                        >
                          {t('table.edit')}
                        </Link>
                        <button
                          style={styles.archiveButton}
                          onClick={() => handleArchive(staffMember.id)}
                        >
                          {t('archive.archive')}
                        </button>
                      </>
                    )}

                    {showArchived && (
                      <>
                        <button
                          style={styles.reactivateButton}
                          onClick={() => handleReactivate(staffMember.id)}
                        >
                          {t('archive.reactivate')}
                        </button>
                        <button
                          style={styles.deleteButton}
                          onClick={() => handlePermanentDelete(staffMember.id)}
                        >
                          {t('archive.deletePermanently')}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // Mobile card list (<768px)
  const renderMobileCards = () => (
    <div style={styles.cardList}>
      {staffList.map(staffMember => {
        const balance = hoursBalances[staffMember.id];
        return (
          <div key={staffMember.id} style={styles.card}>
            <div style={styles.cardName}>
              {staffMember.name}
              {staffMember.active === false && (
                <span style={styles.archivedBadge}>
                  {t('archive.archived')}
                </span>
              )}
            </div>

            <div style={styles.cardRow}>
              <span style={styles.cardLabel}>{t('table.role')}</span>
              <span style={styles.cardValue}>
                {t(`roles.${staffMember.role}`)}
              </span>
            </div>

            <div style={styles.cardRow}>
              <span style={styles.cardLabel}>{t('table.primaryDepartment')}</span>
              <span style={styles.cardValue}>
                {staffMember.primaryDepartmentName}
              </span>
            </div>

            <div style={styles.cardRow}>
              <span style={styles.cardLabel}>{t('table.email')}</span>
              <span style={styles.cardValue}>
                {staffMember.email || t('table.noEmail')}
              </span>
            </div>

            <div style={styles.cardRowLast}>
              <span style={styles.cardLabel}>
                {t('table.monthlyContractHours')}
              </span>
              <span style={styles.cardValue}>
                {staffMember.employmentInfo?.contractHours || 0}{' '}
                {t('table.hours')}
              </span>
            </div>

            <div style={styles.cardRowFinal}>
              <span style={styles.cardLabel}>
                {t('table.hoursBalance', 'Hours Balance')}
              </span>
              <span
                style={{
                  ...styles.cardValue,
                  color:      getHoursBalanceColor(balance),
                  fontWeight: '600'
                }}
              >
                {formatHoursBalance(balance)}
              </span>
            </div>

            <div style={styles.cardActions}>
              <Link
                to={`/admin/staff/${staffMember.id}`}
                style={styles.cardViewButton}
              >
                {t('table.view')}
              </Link>

              {!showArchived && (
                <>
                  <Link
                    to={`/admin/staff/${staffMember.id}/edit`}
                    style={styles.cardEditButton}
                  >
                    {t('table.edit')}
                  </Link>
                  <button
                    style={styles.cardArchiveButton}
                    onClick={() => handleArchive(staffMember.id)}
                  >
                    {t('archive.archive')}
                  </button>
                </>
              )}

              {showArchived && (
                <>
                  <button
                    style={styles.cardReactivateButton}
                    onClick={() => handleReactivate(staffMember.id)}
                  >
                    {t('archive.reactivate')}
                  </button>
                  <button
                    style={styles.cardDeleteButton}
                    onClick={() => handlePermanentDelete(staffMember.id)}
                  >
                    {t('archive.deletePermanently')}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div style={styles.container}>

      {/* Header — stacks vertically below 600px */}
      <div style={styles.header}>
        <h1 style={styles.title}>{t('page.title')}</h1>
        <div style={styles.headerActions}>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            style={styles.refreshButton}
          >
            {isLoading ? t('page.loading') : t('page.refresh')}
          </button>
          <button onClick={handleAddStaff} style={styles.addButton}>
            {t('page.addStaffMember')}
          </button>
        </div>
      </div>

      {/* Filter row — stacks vertically below 600px */}
      <div style={styles.filterContainer}>
        <input
          type="text"
          placeholder={t('page.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          style={styles.departmentSelect}
        >
          <option value="">{t('page.allDepartments')}</option>
          {departments
            .filter(dept => dept && dept.id)
            .map(dept => (
              <option key={dept.id} value={dept.id}>
                {dept.name || 'Unknown'}
              </option>
            ))}
        </select>
      </div>

      {/* ARCHIVE: show-archived toggle */}
      <div style={styles.archiveToggleRow}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          {t('archive.showArchived')}
        </label>
      </div>

      {errorMessage && (
        <div style={styles.errorMessage}>{errorMessage}</div>
      )}

      {/* Content — three mutually exclusive states */}
      {isLoading ? (
        <div style={styles.tableContainer}>
          <div style={styles.loadingIndicator}>
            {loadingDepartments
              ? t('page.loadingDepartments')
              : t('page.loadingStaffMembers')}
          </div>
        </div>
      ) : staffList.length > 0 ? (
        isTablet ? renderMobileCards() : renderTable()
      ) : (
        <div style={styles.tableContainer}>
          <div style={styles.noResults}>
            {searchTerm || departmentFilter
              ? t('page.noMatchingStaff')
              : showArchived
                ? t('archive.noArchivedStaff', 'No archived staff')
                : t('page.noStaffFound')}
          </div>
        </div>
      )}

      {/* ARCHIVE: confirm modals — replace window.confirm() / window.prompt() */}
      <ConfirmModal
        isOpen={modal?.type === 'archive'}
        title={t('archive.archive')}
        message={t('archive.confirmBody')}
        confirmLabel={t('archive.archive')}
        cancelLabel={t('page.cancel', 'Cancel')}
        variant="danger"
        onCancel={() => setModal(null)}
        onConfirm={() => confirmArchive(modal.staffId)}
      />

      <ConfirmModal
        isOpen={modal?.type === 'delete'}
        title={t('archive.deletePermanently')}
        message={t('archive.confirmDeletePermanently')}
        confirmLabel={t('archive.deletePermanently')}
        cancelLabel={t('page.cancel', 'Cancel')}
        variant="danger"
        onCancel={() => setModal(null)}
        onConfirm={() => confirmPermanentDelete(modal.staffId)}
      />

      <ConfirmModal
        isOpen={modal?.type === 'reactivate'}
        title={t('archive.reactivate')}
        message={t('archive.reactivateNeedsNewPin')}
        confirmLabel={t('archive.reactivate')}
        cancelLabel={t('page.cancel', 'Cancel')}
        input={{
          label: t('archive.newPin', 'New PIN'),
          type: 'password',
          maxLength: 4,
          pattern: '^\\d{4}$',
          required: true,
          requiredError: t('archive.pinRequired', 'A PIN is required.'),
          patternError: t('archive.pinMustBe4Digits', 'PIN must be exactly 4 digits.')
        }}
        onCancel={() => setModal(null)}
        onConfirm={(pin) => confirmReactivate(modal.staffId, pin)}
      />

    </div>
  );
};

export default StaffManagementPage;